// Единственное место, где фронтенд разговаривает с бэкендом.
//
// Всё, что знает про адрес, CSRF и формат ошибки, живёт здесь. Компоненты
// не вызывают fetch напрямую: иначе разбор ошибок расползётся по экранам
// и первая же новая ручка обработается не так, как остальные.

const PREFIX = "/api/v1";

// Бэкенд отдаёт ошибку одним форматом на все ручки (src/api/errors.py):
// { "error": { "code": "...", "message": "...", "details": ... } }
export type ApiErrorBody = {
  error: { code: string; message: string; details?: unknown };
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Не «войди заново», а «войди»: сессии нет или она истекла.
   *
   *  `REAUTH_REQUIRED` сюда НЕ входит намеренно. Там владелец вошёл —
   *  просто вошёл «запомнить меня», а действие требует настоящего входа.
   *  Считать это отсутствием сессии значило бы выкинуть его на форму входа
   *  с открытого экрана и потерять то, что он делал. */
  get isUnauthenticated(): boolean {
    return this.status === 401 && this.code !== "REAUTH_REQUIRED";
  }

  /** Вошёл, но по «запомнить меня». Для важного действия этого мало:
   *  выпуск ключа виджета, «выйти на всех устройствах».
   *
   *  Лечится подтверждением пароля на месте (`onReauthNeeded` ниже), а не
   *  повтором запроса и не выходом из кабинета. До появления той ручки
   *  отсюда не было выхода вовсе: метка «вошёл по токену» не снималась
   *  до конца жизни сессии. */
  get isReauthRequired(): boolean {
    return this.code === "REAUTH_REQUIRED";
  }

  /** Долгий вход отключён: предъявленный токен уже был использован.
   *  Это не «сессия истекла», а «у вашей cookie есть копия», и сказать
   *  об этом владельцу надо другими словами. */
  get isRememberReused(): boolean {
    return this.code === "REMEMBER_REUSED";
  }

  /** Вошёл, но данные чужие. Повторный вход не поможет — И-3. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Ручка объявлена контрактом, но ещё не наполнена. */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

/** CSRF-токен лежит в читаемой cookie, сессия — в httpOnly. Пара намеренная:
 *  скрипт со стороннего сайта не прочитает нашу cookie и не подставит
 *  заголовок, а сессию не прочитает даже наш собственный скрипт. */
function csrfToken(): string | null {
  const hit = document.cookie
    .split("; ")
    .find((row) => row.startsWith("salesdept_csrf="));
  return hit ? decodeURIComponent(hit.slice("salesdept_csrf=".length)) : null;
}

type Options = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Не пытаться поднять сессию из «запомнить меня» при 401.
   *  Ставится изнутри — на сам обмен и на ручки входа. */
  noRefresh?: boolean;
};

// Ручки, которым продлевать нечего: сессии у них по определению ещё нет.
// Обмен токена сюда входит первым — иначе неудачный обмен вызывал бы обмен.
const NO_REFRESH = [
  "/auth/refresh",
  "/auth/login",
  "/auth/request-code",
  "/auth/verify-code",
  "/auth/logout",
  "/auth/platform",
];

const ME_PATH = "/auth/me";

// Один обмен на всё приложение, сколько бы запросов ни упёрлось в 401 разом.
//
// Это не оптимизация, а требование безопасности. Токен «запомнить меня»
// одноразовый: два параллельных обмена одним значением на сервере выглядят
// как повторное предъявление — то есть как кража, — и гасят владельцу всю
// цепочку. Открытый кабинет тянет несколько запросов сразу (лиды, агенты,
// статистика), и без этой переменной каждое утро начиналось бы с ложной
// тревоги. Гонку ВКЛАДОК одной страницей не закрыть — её разбирает сервер
// (окно поблажки, src/api/routes/auth.refresh); здесь закрывается гонка
// запросов внутри одной вкладки.
let renewal: Promise<boolean> | null = null;

// Чем кончился последний обмен. Нужен, чтобы отличить «сессия просто
// истекла» от «токеном воспользовались дважды»: второе владельцу надо
// показать словами, а не молчаливой формой входа.
let lastRenewalError: ApiError | null = null;

export function lastRenewalFailure(): ApiError | null {
  return lastRenewalError;
}

async function renewSession(): Promise<boolean> {
  if (renewal === null) {
    renewal = (async () => {
      try {
        await request<void>("/auth/refresh", { method: "POST", noRefresh: true });
        lastRenewalError = null;
        return true;
      } catch (error) {
        lastRenewalError = error instanceof ApiError ? error : null;
        return false;
      }
    })();
  }
  const started = renewal;
  try {
    return await started;
  } finally {
    // Обнуляем только свой же запуск: чужой, начавшийся позже, не трогаем.
    if (renewal === started) renewal = null;
  }
}

// ── Повышение сессии паролем ────────────────────────────────────────────
//
// Тот же приём, что у обмена токена выше, и по той же причине: разбор
// «важному действию нужен настоящий вход» обязан жить в одном месте.
// Разложи его по экранам — и каждая новая защищённая ручка обработается
// не так, как остальные, а какая-нибудь не обработается вовсе. Ровно это
// и было: на экране «Сайт» отказ показывался мёртвой строкой без единой
// кнопки, и владелец упирался в тупик.
//
// Спрашивает пароль не клиент, а тот, кто умеет рисовать, — окно
// подтверждения (`auth/ReauthGate.tsx`). Оно регистрируется здесь один раз
// и возвращает `true`, когда пароль принят.
type PasswordAsker = () => Promise<boolean>;

let askForPassword: PasswordAsker | null = null;

/** Регистрирует окно подтверждения. Возвращает снятие регистрации. */
export function onReauthNeeded(ask: PasswordAsker): () => void {
  askForPassword = ask;
  return () => {
    if (askForPassword === ask) askForPassword = null;
  };
}

// Одно подтверждение на всё приложение, сколько бы запросов ни упёрлось
// в отказ разом. Без этого открытый кабинет показал бы владельцу два окна
// пароля подряд.
let elevation: Promise<boolean> | null = null;

async function elevateSession(): Promise<boolean> {
  const ask = askForPassword;
  if (ask === null) return false;
  if (elevation === null) elevation = ask();
  const started = elevation;
  try {
    return await started;
  } finally {
    if (elevation === started) elevation = null;
  }
}

/** Запрос с одним повтором после 401.
 *
 *  Сначала — попытка поднять сессию из «запомнить меня». Потом запрос
 *  повторяется ровно один раз в любом случае (ADR-18, правило 4): сессию
 *  мог уже поднять сам сервер из сессии платформы — например, параллельный
 *  запрос сменил cookie, пока этот был в пути. Второй 401 — вход
 *  (App.tsx перезапрашивает `/auth/me`). Исключение — сам `/auth/me` без
 *  удачного обмена: сервер решил на этом же запросе, повтор ответит то же.
 *
 *  Повтор безопасен для любого метода, включая POST: вход проверяется
 *  зависимостью ДО тела обработчика (src/api/deps.py), поэтому запрос,
 *  ответивший 401, ничего не сделал. */
export async function api<T>(path: string, options: Options = {}): Promise<T> {
  try {
    return await request<T>(path, options);
  } catch (error) {
    const renewable =
      error instanceof ApiError &&
      error.isUnauthenticated &&
      !options.noRefresh &&
      !NO_REFRESH.some((prefix) => path.startsWith(prefix));
    if (renewable && ((await renewSession()) || path !== ME_PATH)) {
      return await request<T>(path, { ...options, noRefresh: true });
    }
    // Повтор безопасен по тому же доводу, что и выше: вход проверяется
    // зависимостью ДО тела обработчика, поэтому запрос, ответивший отказом,
    // ничего не сделал.
    if (
      error instanceof ApiError &&
      error.isReauthRequired &&
      !path.startsWith("/auth/reauth") &&
      (await elevateSession())
    ) {
      return await request<T>(path, options);
    }
    throw error;
  }
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  // FormData — файл знаний: границу multipart и Content-Type к ней ставит
  // браузер сам, а выставленный руками заголовок эту границу потерял бы.
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) {
    headers["Content-Type"] = "application/json";
  }

  // Заголовок только на изменяющих запросах — ровно там, где его проверяет
  // сервер (src/api/deps.py). На GET он не нужен и только мешал бы кешу.
  if (method !== "GET") {
    const token = csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
  }

  let response: Response;
  try {
    response = await fetch(PREFIX + path, {
      method,
      headers,
      // Относительный путь: в разработке проксирует Vite, в проде это тот же
      // источник. Cookie сессии ведёт себя одинаково в обоих случаях.
      credentials: "same-origin",
      body:
        options.body === undefined
          ? undefined
          : isForm
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    // Сеть не дошла: сервер лежит, прокси не поднят, браузер офлайн.
    // Отдельный код, потому что показать это надо иначе, чем ошибку сервера.
    throw new ApiError(0, "NETWORK", "Сервер не отвечает. Проверьте, запущен ли он.", cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Пришло не JSON — обычно это HTML страницы ошибки прокси.
      // Тело наружу не тащим: в нём нет ничего для владельца кабинета.
      throw new ApiError(response.status, "BAD_RESPONSE", "Сервер ответил неожиданным образом.");
    }
  }

  if (!response.ok) {
    const body = parsed as Partial<ApiErrorBody> | null;
    const error = body?.error;
    throw new ApiError(
      response.status,
      error?.code ?? `HTTP_${response.status}`,
      error?.message ?? "Что-то пошло не так.",
      error?.details,
    );
  }

  return parsed as T;
}
